export const VEHICLE_TELEMETRY_SCHEMA = 'daylight-club.vehicle-telemetry' as const
export const VEHICLE_TELEMETRY_SCHEMA_VERSION = 1 as const
export const VEHICLE_TELEMETRY_CHANNEL = 'daylight-club-telemetry-v1'

export type VehicleTelemetryWheelPosition = 'front-left' | 'front-right' | 'rear-left' | 'rear-right'

export type VehicleTelemetryWheelFrameV1 = Readonly<{
  index: number
  position: VehicleTelemetryWheelPosition
  axle: 'front' | 'rear'
  side: 'left' | 'right'
  contact: boolean
  surfaceGripCoefficient: number
  normalLoadNewtons: number
  steeringAngleRadians: number
  angularVelocityRadiansPerSecond: number
  longitudinalPatchSpeedMetersPerSecond: number
  lateralPatchSpeedMetersPerSecond: number
  slipRatio: number
  slipAngleRadians: number
  frictionCoefficient: number
  combinedSlipNormalized: number
  longitudinalForceNewtons: number
  lateralForceNewtons: number
  driveTorqueNewtonMeters: number
  brakeTorqueNewtonMeters: number
  suspensionCompressionMeters: number
}>

export type VehicleTelemetryFrameV1 = Readonly<{
  schema: typeof VEHICLE_TELEMETRY_SCHEMA
  schemaVersion: typeof VEHICLE_TELEMETRY_SCHEMA_VERSION
  sequence: number
  simulationTimeSeconds: number
  vehicle: Readonly<{
    id: string
    name: string
    physicsModel: string
  }>
  pose: Readonly<{
    positionMeters: Readonly<{ x: number, y: number, z: number }>
    headingRadians: number
    pitchRadians: number
    rollRadians: number
  }>
  motion: Readonly<{
    velocityWorldMetersPerSecond: Readonly<{ x: number, y: number, z: number }>
    velocityBodyMetersPerSecond: Readonly<{ longitudinal: number, lateral: number, vertical: number }>
    accelerationBodyMetersPerSecondSquared: Readonly<{ longitudinal: number, lateral: number }>
    yawRateRadiansPerSecond: number
    bodySlipAngleRadians: number
  }>
  inputs: Readonly<{
    driveCommand: number
    steeringCommand: number
    handbrake: boolean
  }>
  steering: Readonly<{
    roadWheelAngleRadians: number
    slipLimitedMaximumAngleRadians: number
  }>
  powertrain: Readonly<{
    engineAngularVelocityRadiansPerSecond: number
    gear: number
  }>
  chassis: Readonly<{
    groundedWheelCount: number
    supportGrade: string
    surface: string
    longitudinalForceNewtons: number
    lateralForceNewtons: number
  }>
  tyreLimits: Readonly<{
    peakSlipRatio: number
    peakSlipAngleRadians: number
  }>
  wheels: readonly VehicleTelemetryWheelFrameV1[]
}>

export type VehicleTelemetryListener = (frame: VehicleTelemetryFrameV1) => void

function deepFreeze<T>(value: T): T {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value
  Object.freeze(value)
  for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child)
  return value
}

export function freezeVehicleTelemetryFrame(frame: VehicleTelemetryFrameV1) {
  return deepFreeze(frame)
}

export class VehicleTelemetryBus {
  private listeners = new Set<VehicleTelemetryListener>()
  private latestFrame: VehicleTelemetryFrameV1 | undefined

  publish(frame: VehicleTelemetryFrameV1) {
    this.latestFrame = frame
    for (const listener of this.listeners) {
      try {
        listener(frame)
      } catch (error) {
        console.warn('[Daylight Telemetry] A consumer rejected a frame.', error)
      }
    }
  }

  latest() {
    return this.latestFrame
  }

  subscribe(listener: VehicleTelemetryListener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
}

export class VehicleTelemetryRingRecorder {
  readonly capacity: number
  private buffer: Array<VehicleTelemetryFrameV1 | undefined>
  private writeIndex = 0
  private frameCount = 0
  private active = false

  constructor(seconds = 30, framesPerSecond = 60) {
    this.capacity = Math.max(1, Math.ceil(seconds * framesPerSecond))
    this.buffer = new Array(this.capacity)
  }

  start() {
    this.clear()
    this.active = true
  }

  stop() {
    this.active = false
    return this.frames()
  }

  capture(frame: VehicleTelemetryFrameV1) {
    if (!this.active) return
    this.buffer[this.writeIndex] = frame
    this.writeIndex = (this.writeIndex + 1) % this.capacity
    this.frameCount = Math.min(this.capacity, this.frameCount + 1)
  }

  clear() {
    this.buffer.fill(undefined)
    this.writeIndex = 0
    this.frameCount = 0
  }

  isRecording() {
    return this.active
  }

  size() {
    return this.frameCount
  }

  durationSeconds() {
    if (this.frameCount < 2) return 0
    const frames = this.frames()
    return frames[frames.length - 1].simulationTimeSeconds - frames[0].simulationTimeSeconds
  }

  frames() {
    const frames: VehicleTelemetryFrameV1[] = []
    const firstIndex = this.frameCount === this.capacity ? this.writeIndex : 0
    for (let offset = 0; offset < this.frameCount; offset += 1) {
      const frame = this.buffer[(firstIndex + offset) % this.capacity]
      if (frame) frames.push(frame)
    }
    return frames
  }
}

export function connectTelemetryBroadcast(
  bus: VehicleTelemetryBus,
  channelName = VEHICLE_TELEMETRY_CHANNEL,
) {
  if (typeof BroadcastChannel === 'undefined') return { available: false, close: () => undefined }
  const channel = new BroadcastChannel(channelName)
  const unsubscribe = bus.subscribe((frame) => channel.postMessage(frame))
  return {
    available: true,
    close: () => {
      unsubscribe()
      channel.close()
    },
  }
}

export function downloadTelemetryNdjson(frames: readonly VehicleTelemetryFrameV1[], filename: string) {
  const body = frames.map((frame) => JSON.stringify(frame)).join('\n') + (frames.length > 0 ? '\n' : '')
  const url = URL.createObjectURL(new Blob([body], { type: 'application/x-ndjson' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}
